import { useEffect, useRef, useState } from 'preact/hooks';
import { useOddball } from '../midi-context';
import type { SynthMode } from '../midi-context';
import { getAudioContext, createReverbBuffer, createNoiseBuffer } from '../audio/audio-engine';
import { playSoundfontNote, freqToMidi, isInstrumentLoaded, getCurrentProgram } from '../audio/soundfont-player';
import { useCCValues } from '../hooks/use-oddball-events';
import type { GestureEvent, RollingEvent, TapEvent, ShakeEvent, TwistEvent, CCUpdateEvent } from '../../src/types';
import { OddballCC } from '../../src/types';

// Scales
const MAJOR_PENT = [261.63, 293.66, 329.63, 392.00, 440.00, 523.25, 587.33, 659.25];
const MINOR_PHRYG = [261.63, 277.18, 311.13, 349.23, 392.00, 415.30, 466.16, 523.25];

function mapRange(value: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  return outMin + ((value - inMin) / (inMax - inMin)) * (outMax - outMin);
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function getQuadrantName(valence: number, energy: number): string {
  if (valence >= 0 && energy < 0) return 'Bright + Calm';
  if (valence >= 0 && energy >= 0) return 'Bright + Intense';
  if (valence < 0 && energy < 0) return 'Dark + Calm';
  return 'Dark + Intense';
}

function getScaleName(valence: number): string {
  return valence >= 0 ? 'Major Pentatonic' : 'Phrygian';
}

interface MoodEngine {
  ctx: AudioContext;
  masterGain: GainNode;
  reverb: ConvolverNode;
  wetGain: GainNode;
  // Note triggering state
  filterCutoff: number;
  lfoRate: number;
  sawMix: number; // 0 = pure sine, 1 = mostly sawtooth
  // Noise layer
  noiseSource: AudioBufferSourceNode | null;
  noiseGain: GainNode;
  noiseFilter: BiquadFilterNode;
  noiseBuffer: AudioBuffer;
  // Current note tracking
  currentDegree: number;
  lastNoteTime: number; // minimum interval between triggered notes
  octaveShift: number;
  speedMul: number;
}

function createMoodEngine(): MoodEngine {
  const ctx = getAudioContext();
  const masterGain = ctx.createGain();
  masterGain.gain.value = 0.35;

  const reverb = ctx.createConvolver();
  reverb.buffer = createReverbBuffer(ctx, 3, 2.5);
  const wetGain = ctx.createGain();
  wetGain.gain.value = 0.5;
  const dryGain = ctx.createGain();
  dryGain.gain.value = 0.5;
  masterGain.connect(dryGain).connect(ctx.destination);
  masterGain.connect(reverb).connect(wetGain).connect(ctx.destination);

  // Noise layer
  const noiseBuffer = createNoiseBuffer(ctx, 2);
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 1000;
  noiseFilter.Q.value = 0.5;
  const noiseGain = ctx.createGain();
  noiseGain.gain.value = 0;
  noiseFilter.connect(noiseGain).connect(masterGain);

  return {
    ctx, masterGain, reverb, wetGain,
    filterCutoff: 1500, lfoRate: 1, sawMix: 0,
    noiseSource: null, noiseGain, noiseFilter, noiseBuffer,
    currentDegree: -1,
    lastNoteTime: 0,
    octaveShift: 0, speedMul: 1,
  };
}

function startNoise(eng: MoodEngine) {
  if (eng.noiseSource) return;
  const source = eng.ctx.createBufferSource();
  source.buffer = eng.noiseBuffer;
  source.loop = true;
  source.connect(eng.noiseFilter);
  source.start();
  eng.noiseSource = source;
}

// Trigger a discrete piano-like note
function playNote(eng: MoodEngine, freq: number, velocity = 0.8, synthMode: SynthMode = 'oscillator') {
  const { ctx, masterGain } = eng;
  const t = ctx.currentTime;

  // Decay time: calm = longer ring, intense = shorter
  const decay = mapRange(eng.lfoRate, 0.5, 8, 2.0, 0.6);
  const vol = 0.18 * velocity;

  const octMul = Math.pow(2, eng.octaveShift);

  if (synthMode === 'soundfont' && isInstrumentLoaded(getCurrentProgram())) {
    playSoundfontNote(masterGain, freqToMidi(freq * octMul), decay, vol);
  } else {
    // Sine component (always present, piano-like fundamental)
    const sine = ctx.createOscillator();
    sine.type = 'sine';
    sine.frequency.value = freq * octMul;
    const sineG = ctx.createGain();
    sineG.gain.setValueAtTime(0, t);
    sineG.gain.linearRampToValueAtTime(vol, t + 0.008);
    sineG.gain.exponentialRampToValueAtTime(vol * 0.4, t + 0.06);
    sineG.gain.exponentialRampToValueAtTime(0.001, t + decay);

    // Filter per note
    const noteFilter = ctx.createBiquadFilter();
    noteFilter.type = 'lowpass';
    noteFilter.frequency.value = eng.filterCutoff;
    noteFilter.Q.value = 0.7;

    sine.connect(sineG).connect(noteFilter).connect(masterGain);
    sine.start(t);
    sine.stop(t + decay + 0.05);

    // Sawtooth/harmonic component (fades in with dark+intense mood)
    if (eng.sawMix > 0.05) {
      const saw = ctx.createOscillator();
      saw.type = 'sawtooth';
      saw.frequency.value = freq * octMul;
      const sawG = ctx.createGain();
      const sawVol = vol * eng.sawMix * 0.5;
      sawG.gain.setValueAtTime(0, t);
      sawG.gain.linearRampToValueAtTime(sawVol, t + 0.008);
      sawG.gain.exponentialRampToValueAtTime(sawVol * 0.3, t + 0.04);
      sawG.gain.exponentialRampToValueAtTime(0.001, t + decay * 0.7);
      saw.connect(sawG).connect(noteFilter);
      saw.start(t);
      saw.stop(t + decay + 0.05);
    }

    // Brief harmonic "ping" for attack brightness
    const ping = ctx.createOscillator();
    ping.type = 'sine';
    ping.frequency.value = freq * octMul * 3; // 3rd harmonic
    const pingG = ctx.createGain();
    const pingVol = vol * 0.12;
    pingG.gain.setValueAtTime(0, t);
    pingG.gain.linearRampToValueAtTime(pingVol, t + 0.003);
    pingG.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    ping.connect(pingG).connect(noteFilter);
    ping.start(t);
    ping.stop(t + 0.1);
  }
}

function applyMood(eng: MoodEngine, valence: number, energy: number) {
  const t = eng.ctx.currentTime;

  // Store mood-derived parameters for note triggering
  eng.sawMix = clamp((-valence + 1) / 2 * (energy + 1) / 2, 0, 1);
  eng.filterCutoff = mapRange(energy, -1, 1, 600, 5000);
  eng.lfoRate = mapRange(energy, -1, 1, 0.5, 8);

  // Reverb: calm=lots, intense=dry
  const wet = mapRange(energy, -1, 1, 0.7, 0.15);
  eng.wetGain.gain.setTargetAtTime(wet, t, 0.15);

  // Noise layer: fades in as energy increases and valence decreases
  const noiseVol = clamp(((energy + 1) / 2) * ((-valence + 1) / 2) * 0.1, 0, 0.1);
  eng.noiseGain.gain.setTargetAtTime(noiseVol, t, 0.15);
}

// Energy-dependent note speed: calm = slow (500ms), intense = fast (100ms)
function getMinNoteInterval(energy: number): number {
  // energy: -1 (calm) to +1 (intense)
  return mapRange(clamp(energy, -1, 1), -1, 1, 500, 100);
}

// When calm and input is too fast, add expression instead of a note
function addCalmExpression(eng: MoodEngine) {
  const t = eng.ctx.currentTime;
  // Brief reverb swell
  const currentWet = eng.wetGain.gain.value;
  eng.wetGain.gain.setTargetAtTime(Math.min(currentWet + 0.15, 0.9), t, 0.05);
  eng.wetGain.gain.setTargetAtTime(currentWet, t + 0.3, 0.2);
  // Filter resonance bump
  eng.noiseFilter.Q.setTargetAtTime(2.5, t, 0.03);
  eng.noiseFilter.Q.setTargetAtTime(0.5, t + 0.2, 0.15);
}

// Returns the new degree if it changed, or -1 if same
function updatePitch(eng: MoodEngine, valence: number, energy: number, orientX: number, synthMode: SynthMode): number {
  const scale = valence >= 0 ? MAJOR_PENT : MINOR_PHRYG;
  const degree = Math.floor(mapRange(orientX, 0, 127, 0, scale.length - 0.01));
  const clamped = clamp(degree, 0, scale.length - 1);
  if (clamped === eng.currentDegree) return -1;
  const now = performance.now();
  const minInterval = getMinNoteInterval(energy) / eng.speedMul;
  if (now - eng.lastNoteTime < minInterval) {
    // In calm mode, express the fast input as texture instead
    if (energy < -0.2) addCalmExpression(eng);
    return -1;
  }
  eng.currentDegree = clamped;
  eng.lastNoteTime = now;
  const freq = scale[clamped];
  playNote(eng, freq, 0.8, synthMode);
  return clamped;
}

export function MoodDrift() {
  const { parser, detector, connected, speedModifier, octaveOffset, synthMode } = useOddball();
  const cc = useCCValues();
  const engineRef = useRef<MoodEngine | null>(null);
  const speedRef = useRef(1.0);
  const octaveRef = useRef(0);
  const synthModeRef = useRef<SynthMode>('oscillator');

  useEffect(() => { speedRef.current = speedModifier; }, [speedModifier]);
  useEffect(() => { octaveRef.current = octaveOffset; }, [octaveOffset]);
  useEffect(() => { synthModeRef.current = synthMode; }, [synthMode]);
  useEffect(() => {
    const eng = engineRef.current;
    if (eng) eng.masterGain.gain.setTargetAtTime(connected ? 0.35 : 0, eng.ctx.currentTime, 0.05);
  }, [connected]);

  const [valence, setValence] = useState(0);
  const [energy, setEnergy] = useState(0);
  const valenceRef = useRef(0);
  const energyRef = useRef(0);

  useEffect(() => {
    const eng = createMoodEngine();
    engineRef.current = eng;
    startNoise(eng);
    applyMood(eng, 0, 0);

    // Faster decay toward neutral (~15s from extremes)
    const driftInterval = window.setInterval(() => {
      let changed = false;
      if (Math.abs(valenceRef.current) > 0.001) {
        valenceRef.current *= 0.995;
        changed = true;
      }
      if (Math.abs(energyRef.current) > 0.001) {
        energyRef.current *= 0.995;
        changed = true;
      }
      if (changed) {
        setValence(valenceRef.current);
        setEnergy(energyRef.current);
        applyMood(eng, valenceRef.current, energyRef.current);
      }
    }, 50);

    const shiftMood = (dv: number, de: number) => {
      valenceRef.current = clamp(valenceRef.current + dv, -1, 1);
      energyRef.current = clamp(energyRef.current + de, -1, 1);
      setValence(valenceRef.current);
      setEnergy(energyRef.current);
      applyMood(eng, valenceRef.current, energyRef.current);
    };

    // Auto-melody: gentle wandering near current note, biased downward
    let autoMelodyIndex = 3; // start mid-scale
    let lastOrientXTime = 0;
    let lastOrientXValue = 64;

    // Anti-repetition: after 5 same notes, force alternation
    let lastAutoNote = -1;
    let sameNoteCount = 0;
    let autoAlternate = false;
    let alternateDirection = 1; // +1 or -1

    // Dark motifs: short melodic fragments as index offsets from current position
    const DARK_MOTIFS = [
      [0, -1, 0, -2],       // step down, return, drop further
      [0, 1, 0, -1],        // neighbor tone oscillation
      [0, -2, -1, 0],       // drop and climb back
      [0, -1, -2, -1],      // descending turn
      [0, 2, 0, -1],        // leap up then settle down
      [0, -1, -3, -2],      // falling with a catch
    ];
    let darkMotif: number[] | null = null;
    let darkMotifStep = 0;
    let darkMotifBase = 3;

    // Reset auto-alternate when user provides input
    const resetAutoAlternate = () => {
      if (autoAlternate) {
        autoAlternate = false;
        sameNoteCount = 0;
      }
    };

    let autoMelodyTimeout = 0;
    const scheduleAutoMelody = () => {
      autoMelodyTimeout = window.setTimeout(() => {
        const now = Date.now();
        if (now - lastOrientXTime >= 1500) {
          const isDark = valenceRef.current < -0.15;
          const scale = valenceRef.current >= 0 ? MAJOR_PENT : MINOR_PHRYG;
          autoMelodyIndex = clamp(autoMelodyIndex, 0, scale.length - 1);

          if (autoAlternate) {
            autoMelodyIndex = clamp(autoMelodyIndex + alternateDirection, 0, scale.length - 1);
            if (autoMelodyIndex <= 0 || autoMelodyIndex >= scale.length - 1) {
              alternateDirection = -alternateDirection;
            }
            alternateDirection = -alternateDirection;
          } else if (isDark) {
            if (!darkMotif || darkMotifStep >= darkMotif.length) {
              darkMotif = DARK_MOTIFS[Math.floor(Math.random() * DARK_MOTIFS.length)];
              darkMotifStep = 0;
              darkMotifBase = autoMelodyIndex;
            }
            autoMelodyIndex = clamp(darkMotifBase + darkMotif[darkMotifStep], 0, scale.length - 1);
            darkMotifStep++;
          } else {
            darkMotif = null;
            const r = Math.random();
            if (r < 0.45 && autoMelodyIndex > 0) {
              autoMelodyIndex--;
            } else if (r < 0.65) {
              // stay
            } else if (r < 0.85 && autoMelodyIndex < scale.length - 1) {
              autoMelodyIndex++;
            }
            if (autoMelodyIndex > 4 && Math.random() < 0.3) {
              autoMelodyIndex--;
            }
          }

          if (autoMelodyIndex === lastAutoNote) {
            sameNoteCount++;
            if (sameNoteCount >= 5) {
              autoAlternate = true;
              alternateDirection = autoMelodyIndex > 0 ? -1 : 1;
            }
          } else {
            sameNoteCount = 0;
          }
          lastAutoNote = autoMelodyIndex;

          const freq = scale[autoMelodyIndex];
          playNote(eng, freq, 0.5, synthModeRef.current);
        }
        scheduleAutoMelody();
      }, 1800 / eng.speedMul);
    };
    scheduleAutoMelody();

    // Tap velocity differentiates
    let lastTapTime = 0;
    let rapidTapCount = 0;

    const onTap = (evt: TapEvent) => {
      resetAutoAlternate();
      const now = Date.now();
      if (now - lastTapTime < 400) {
        rapidTapCount++;
      } else {
        rapidTapCount = 1;
      }
      lastTapTime = now;

      if (rapidTapCount >= 3) {
        shiftMood(0.3, 0.15);
        rapidTapCount = 0;
      } else if (evt.velocity > 80) {
        shiftMood(0.2, 0.15);
      } else {
        // Soft tap = bright + slightly calmer
        shiftMood(0.1, -0.05);
      }
    };

    const onShake = (evt: ShakeEvent) => {
      resetAutoAlternate();
      const scale = evt.velocity / 127;
      shiftMood(0, 0.1 + scale * 0.15);
    };

    const onTwist = (evt: TwistEvent) => {
      resetAutoAlternate();
      // Twist = darker AND calmer; strong twist = deep melancholy
      if (evt.velocity > 90) {
        shiftMood(-0.25, -0.1);
      } else {
        shiftMood(-0.12, -0.08);
      }
    };

    const onGesture = (evt: GestureEvent) => {
      switch (evt.type) {
        case 'caught':
          shiftMood(0.3, 0);
          break;
        case 'landed':
          shiftMood(-0.3, 0);
          break;
        case 'sustainedShake':
          shiftMood(0, 0.35);
          break;
        case 'sustainedTwist':
          // Sustained twist = deep dark + calm
          shiftMood(-0.35, -0.2);
          break;
        case 'rollStart':
          // Rolling = calming (initial nudge)
          shiftMood(0, -0.15);
          break;
        case 'rolling': {
          // Continuous calming proportional to oscillation frequency
          const rollingEvt = evt as RollingEvent;
          shiftMood(0, -0.01 * rollingEvt.oscillationFreq);
          break;
        }
        case 'idle':
          // Stillness = calm
          shiftMood(0, -0.1);
          break;
      }
    };

    const onCC = (evt: CCUpdateEvent) => {
      eng.octaveShift = octaveRef.current;
      eng.speedMul = speedRef.current;
      const t = eng.ctx.currentTime;
      switch (evt.cc) {
        case OddballCC.OrientX: {
          resetAutoAlternate();
          lastOrientXTime = Date.now();
          lastOrientXValue = evt.value;
          updatePitch(eng, valenceRef.current, energyRef.current, evt.value, synthModeRef.current);
          break;
        }
        case OddballCC.OrientY: {
          // OrientY modulates noise filter for textural variation
          const cutoff = mapRange(evt.value, 0, 127, 400, 3000);
          eng.noiseFilter.frequency.setTargetAtTime(cutoff, t, 0.05);
          break;
        }
      }
    };

    parser.on('tap', onTap);
    parser.on('shake', onShake);
    parser.on('twist', onTwist);
    detector.on('gesture', onGesture);
    parser.on('cc', onCC);

    return () => {
      clearInterval(driftInterval);
      clearTimeout(autoMelodyTimeout);
      parser.off('tap', onTap);
      parser.off('shake', onShake);
      parser.off('twist', onTwist);
      detector.off('gesture', onGesture);
      parser.off('cc', onCC);
      if (eng.noiseSource) { try { eng.noiseSource.stop(); } catch {} }
      eng.masterGain.disconnect();
    };
  }, [parser, detector]);

  const quadrant = getQuadrantName(valence, energy);
  const scaleName = getScaleName(valence);

  // Color mapping: bright=warm, dark=cool, calm=desat, intense=sat
  const hue = mapRange(valence, -1, 1, 220, 40);
  const sat = mapRange(Math.abs(energy), 0, 1, 20, 80);
  const light = mapRange(energy, -1, 1, 15, 30);

  // Dot position in the 2D grid
  const dotX = ((valence + 1) / 2) * 100;
  const dotY = ((1 - (energy + 1) / 2)) * 100;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', paddingTop: '32px',
      minHeight: '100%',
      background: `radial-gradient(ellipse at center, hsl(${hue}, ${sat}%, ${light}%), transparent)`,
      borderRadius: '12px', padding: '40px', transition: 'background 0.5s',
    }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888' }}>Mood Drift</h2>

      {/* 2D mood space */}
      <div style={{ position: 'relative', width: '200px', height: '200px', border: '1px solid #333', borderRadius: '4px', background: '#111' }}>
        {/* Axis labels */}
        <div style={{ position: 'absolute', top: '-18px', left: '50%', transform: 'translateX(-50%)', fontSize: '0.6rem', color: '#666' }}>intense</div>
        <div style={{ position: 'absolute', bottom: '-18px', left: '50%', transform: 'translateX(-50%)', fontSize: '0.6rem', color: '#666' }}>calm</div>
        <div style={{ position: 'absolute', left: '-30px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.6rem', color: '#666' }}>dark</div>
        <div style={{ position: 'absolute', right: '-32px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.6rem', color: '#666' }}>bright</div>
        {/* Grid lines */}
        <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, borderLeft: '1px solid #222' }} />
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px solid #222' }} />
        {/* Mood dot */}
        <div style={{
          position: 'absolute',
          left: `${dotX}%`, top: `${dotY}%`,
          width: '12px', height: '12px', borderRadius: '50%',
          background: `hsl(${hue}, ${sat + 20}%, 60%)`,
          transform: 'translate(-50%, -50%)',
          transition: 'left 0.15s, top 0.15s, background 0.3s',
          boxShadow: `0 0 8px hsl(${hue}, ${sat + 20}%, 50%)`,
        }} />
      </div>

      {/* Quadrant + scale */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '1rem', fontWeight: 300, color: `hsl(${hue}, ${sat + 20}%, 65%)`, transition: 'color 0.3s' }}>
          {quadrant}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '4px' }}>
          {scaleName}
        </div>
      </div>

      {/* Valence / energy readouts */}
      <div style={{ display: 'flex', gap: '24px', fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>
        <span>V: {valence.toFixed(2)}</span>
        <span>E: {energy.toFixed(2)}</span>
      </div>

      <div style={{ fontSize: '0.7rem', color: '#444', maxWidth: '320px', textAlign: 'center', lineHeight: 1.6 }}>
        Soft tap = bright + calm &middot; Hard tap = bright + intense &middot; Rapid taps = euphoric
        <br />
        Twist = darker + calmer &middot; Shake = intense &middot; Roll/still = calm
      </div>
    </div>
  );
}
